import { ROUTES } from '@/routes';
import { themeColors } from '@/theme/colors';
import { Button } from '@lego/connect-components-react';
import { Flex, Image, Text } from '@lego/klik-ui';
import type { ReactElement } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
export const ErrorPage = ({
  error,
  resetErrorBoundary,
}: FallbackProps): ReactElement => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate(ROUTES.Home.path);
    resetErrorBoundary();
  };

  return (
    <Flex
      maxW="100%"
      minHeight="100vh"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      backgroundColor={themeColors.surface.background.light}
    >
      <Flex flexDirection="column" alignItems="center" gap="80px">
        <Image src="/static/error-page-oops.svg" loading="eager" />
        <Flex
          justifyContent="center"
          alignItems="center"
          flexDir="column"
          gap="32px"
        >
          <Text
            fontWeight="bold"
            fontSize="4xl"
            mb={2}
            color={themeColors.content.default}
          >
            Beep boop, malfunction alert!
          </Text>
          <Text
            fontSize="larger"
            textAlign="center"
            color={themeColors.content.subtle}
          >
            Hmm, this glitch caught me off guard! <br /> Maybe a quick
            turnaround and a return visit will give us time to sort things out.{' '}
            <br /> Feel free to refresh the page and cross your fingers for a
            quick fix!
          </Text>
          <Flex gap="40px">
            <Button
              onClick={() => window.location.reload()}
              variant="primary"
              appearance="outline"
              aria-label="Reload this page"
              size="large"
            >
              Reload this page
            </Button>
            <Button
              onClick={handleGoHome}
              variant="primary"
              appearance="solid"
              aria-label="Go to homepage"
              size="large"
            >
              Go to homepage
            </Button>
          </Flex>
          {import.meta.env.DEV && error instanceof Error && (
            <Flex
              direction="column"
              marginTop="16px"
              padding="16px"
              borderRadius="md"
              alignItems="center"
            >
              <Text fontWeight="bold" fontSize="sm" mb={1}>
                Error details (dev only):
              </Text>
              <details style={{ width: '100%', textAlign: 'center' }}>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: themeColors.content.negative,
                    display: 'inline-block',
                    listStyle: 'none',
                  }}
                >
                  {error.message}{' '}
                  <span aria-hidden="true"> - Click me for more info</span>
                </summary>
                <div
                  style={{
                    maxHeight: '350px',
                    overflowY: 'auto',
                    marginTop: '8px',
                    textAlign: 'left',
                  }}
                >
                  <Text
                    fontSize="xs"
                    fontFamily="mono"
                    color={themeColors.content.default}
                    whiteSpace="pre-wrap"
                  >
                    {error.stack}
                  </Text>
                </div>
              </details>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
